import React, { useState, useEffect } from "react";
import { VscUnverified } from "react-icons/vsc";
import { MdOutlineSendToMobile } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Empty, Modal } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import CryptoJS from "crypto-js";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { config } from "../../config/config.jsx";
import Swal from "sweetalert2";
import { format } from "date-fns";
import LogoImg from "../../assets/images/Logo.png";

function PhoneBVN() {
  /* ---------------------------- component state ---------------------------- */
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(true);
  const [amount, setAmount] = useState(100); // Default agent fee
  const [historyLoading, setHistoryLoading] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  // Get userId from encrypted localStorage
  let userId = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
    }
  } catch (error) {}

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone || phone.length !== 11) {
      toast.error("Please enter a valid 11-digit phone number");
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Confirm Manual Verification",
      html: `
        <p class="mb-4 text-gray-500">Are you sure you want to submit this Phone BVN manual verification request?</p>
        <div class="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2 text-left mb-4 font-mono text-sm">
          <p class="text-sm text-gray-600"><strong>Service:</strong> Phone BVN (Manual)</p>
          <p class="text-sm text-gray-600"><strong>Phone:</strong> ${phone}</p>
        </div>
        <div class="bg-sky-50 border-2 border-sky-200 rounded-2xl py-3 px-6 inline-block shadow-sm">
          <span class="text-xs font-bold text-sky-500 uppercase tracking-wider block mb-0.5">Verification Fee</span>
          <span class="text-3xl font-black text-sky-600">₦${amount.toLocaleString()}</span>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    const payload = {
      phone,
      amount,
      userId,
    };

    setLoading(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.bvnManualWithPhone}`,
        payload,
        { withCredentials: true }
      );

      await Swal.fire({
        title: "Submission Successful!",
        text: response.data?.message || "Your Phone BVN request has been submitted successfully.",
        icon: "success",
        confirmButtonColor: "#f59e0b",
      });

      setPhone("");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Verification failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch prices effect
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        
        // Find BVN with Phone pricing
        const phoneBvnPricing = response.data.find((item) => item.key === "bvn_phone");

        if (phoneBvnPricing) {
          setAmount(phoneBvnPricing.prices?.agent || 100);
        }
      } catch (error) {
        
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  // Fetch history effect
  useEffect(() => {
    const fetchHistory = async () => {
      if (!userId) return;
      setHistoryLoading(true);
      try {
        const apiLink = `${config.apiBaseUrl}${config.endpoints.DataHistory}${userId}`;
        const response = await axios.get(apiLink, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });

        const details = response.data?.findData || [];
        // Filter history specifically for manual phone bvn transactions
        const filtered = details.filter((item) => {
          const type = (item.dataFor || "").toLowerCase();
          const detail = (item.data?.detail || "").toLowerCase();
          return (
            type.includes("phone-bvn") ||
            type.includes("bvn-phone") ||
            type.includes("bvn-with-phone") ||
            (type.includes("bvn") && detail.includes("phone"))
          );
        });

        setApiData(filtered);
      } catch (error) {
        
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [userId, refreshKey]);

  const showModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalVisible(true);
  };

  /* --------------------------------- render -------------------------------- */
  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6 border-b pb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Phone BVN Verification (Manual)</h2>
            <p className="text-sm text-gray-500 mt-1">Submit Phone number to manually verify BVN details</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto mt-5">
          <div className="mb-6 p-4 rounded-2xl bg-sky-50 border-2 border-sky-200 text-sky-600 text-center shadow-sm max-w-sm mx-auto">
            <span className="text-xs font-bold uppercase tracking-wider block mb-0.5">Verification Fee</span>
            <span className="font-black text-2xl">
              ₦{priceLoading ? "..." : amount.toLocaleString()}
            </span>
          </div>

          <form onSubmit={handleSubmit}>
            <p className="mt-7 text-[14px] font-medium text-gray-600 mb-2">
              Supply Phone Number
            </p>
            <div className="flex flex-row justify-start gap-3 items-center my-5">
              <p className="bg-purple-700 p-2 rounded-full text-white">
                <VscUnverified className="text-3xl" />
              </p>
              <div>
                <p className="text-gray-500 text-[16px] mb-1">Watch Information</p>
                <p className="text-gray-500 text-[16px]">
                  Please supply the phone number registered with the BVN.
                </p>
              </div>
            </div>
            
            <input
              type="text"
              className="pl-4 py-3 border border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 rounded-lg w-full outline-none transition-all"
              placeholder="PHONE NUMBER"
              required
              name="phone"
              inputMode="numeric"
              autoComplete="off"
              pattern="\d{11}"
              maxLength="11"
              title="Phone must be exactly 11 digits"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/, ""))}
            />

            <p className="text-gray-400 text-xs mt-3 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              We'll never share your details with anyone else.
            </p>
            
            <label className="flex items-start mt-8 space-x-3 cursor-pointer">
              <span className="relative">
                <input
                  type="checkbox"
                  className="peer shrink-0 appearance-none h-5 w-5 border border-gray-400 rounded-sm bg-white checked:bg-blue-600 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  required
                  title="Required"
                />
                <svg
                  className="absolute w-4 h-4 text-white left-0.5 top-0.5 pointer-events-none hidden peer-checked:block"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.586l7.879-7.879a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <span className="text-sm text-gray-400">
                By checking this box, you agreed that the owner of the ID has
                granted you consent to verify his/her identity.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className={`flex items-center text-xl mt-10 mb-8 cursor-pointer justify-center gap-2 ${
                loading ? "bg-gray-400" : "bg-sky-500 hover:bg-sky-600"
              } text-white font-medium py-2 px-4 rounded-xl w-full h-[50px] transition-colors`}
            >
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin" />
              ) : (
                <MdOutlineSendToMobile className="" />
              )}
              {loading ? "Submitting..." : "Submit Verification"}
            </button>
          </form>
        </div>
        <ToastContainer />
      </div>

      {/* History Table */}
      <div className="rounded-2xl bg-white p-6 shadow-lg border border-gray-100">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Phone BVN Verification History</h2>
        
        {historyLoading && (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        )}

        {!historyLoading && apiData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">S/N</th>
                  <th scope="col" className="px-6 py-3">Phone Number</th>
                  <th scope="col" className="px-6 py-3">Amount</th>
                  <th scope="col" className="px-6 py-3">Status</th>
                  <th scope="col" className="px-6 py-3">Date</th>
                  <th scope="col" className="px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {apiData.map((item, index) => (
                  <tr key={item._id} className="bg-white border-b hover:bg-gray-50">
                    <td className="px-6 py-4">{index + 1}</td>
                    <td className="px-6 py-4 font-mono">
                      {item.data?.phone || item.data?.user_data?.searchParameter || "N/A"}
                    </td>
                    <td className="px-6 py-4">₦{(item.amount || 100).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                        String(item.status || "").toLowerCase() === "successfully" || String(item.status || "").toLowerCase() === "success"
                          ? "bg-green-100 text-green-800"
                          : String(item.status || "").toLowerCase() === "pending"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {item.status || "Pending"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {format(new Date(item.createdAt), "dd/MM/yyyy HH:mm")}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => showModal(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeOutlined className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !historyLoading ? (
          <div className="py-8">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No history records found" />
          </div>
        ) : null}
      </div>

      {/* Details Modal */}
      <Modal
        title="Phone BVN Verification Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={500}
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Date Submitted</p>
                <p className="mt-1 text-sm text-gray-900">
                  {format(new Date(selectedTransaction.createdAt), "dd/MM/yyyy HH:mm:ss")}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <span className={`mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full inline-block ${
                  String(selectedTransaction.status || "").toLowerCase() === "successfully" || String(selectedTransaction.status || "").toLowerCase() === "success"
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}>
                  {selectedTransaction.status || "Pending"}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone Number</p>
                <p className="mt-1 text-sm text-gray-900 font-mono">
                  {selectedTransaction.data?.phone || selectedTransaction.data?.user_data?.searchParameter || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Amount Charged</p>
                <p className="mt-1 text-sm text-gray-900">
                  ₦{(selectedTransaction.amount || 100).toLocaleString()}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-500">Reference</p>
                <p className="mt-1 text-sm text-gray-900 font-mono">
                  {selectedTransaction.data?.verification?.reference || selectedTransaction.data?.user_data?.transactionReference || "N/A"}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-500">Response Detail</p>
                <p className="mt-1 text-sm text-gray-900 bg-slate-50 p-3 rounded-lg border border-slate-100 font-mono text-xs">
                  {selectedTransaction.data?.detail || selectedTransaction.data?.message || "Under review by admin."}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default PhoneBVN;
